import React from 'react';
import injectSheet from 'react-jss';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';

import AssetsError from './AssetsError';
import EmptyAssets from './EmptyAssets';
import AssetLoadMore from './AssetLoadMore';
import Asset from './Asset';
import SortingView from '../sorting/SortingView';
import AssetActions from './AssetActions';
import BatchCopyToModal from '../modals/BatchCopyToModal';
import BatchDuplicateModal from '../modals/BatchDuplicateModal';
import BatchMoveToModal from '../modals/BatchMoveToModal';
import BatchDeleteModal from '../modals/BatchDeleteModal';
import BatchTagModal from '../modals/BatchTagModal';
import BatchPublishModal from '../modals/BatchPublishModal';
import LoadingModal from '../modals/LoadingModal';
import ExportImagesModal from '../modals/ExportImagesModal';
import ExportImagesConfirmationModal from '../modals/ExportImagesConfirmationModal';

import { BATCH_ACTION_TYPES } from '../../enums/AssetBatchActionTypes';
import color from '@material-ui/core/colors/amber';

class Assets extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedAssetsIds: new Set(),
            lastSelectedIndex: undefined,
            application: props.application,
            activeModalType: null,
            selectedAssets: [],
            showExportImagesModal: false,
            showExportImagesConfirmation: false,
            lastExportSelection: null,
        };

        this.loadMoreTriggerRef = React.createRef();
        this.intersectionObserver = null;
    }

    componentDidMount() {
        this.props.onFetchTags();
        this.setupLazyLoading();
    }

    componentDidUpdate(prevProps) {
        // If the tab changes, reset the selected assets
        if (this.props.application !== prevProps.application) {
            this.setState({
                selectedAssetsIds: new Set(),
                lastSelectedIndex: undefined,
            });
        }

        if (prevProps.canLoadMore !== this.props.canLoadMore) {
            this.setupLazyLoading();
        }
    }

    componentWillUnmount() {
        this.cleanupLazyLoading();
    }

    setupLazyLoading = () => {
        this.cleanupLazyLoading();

        if (this.loadMoreTriggerRef.current && this.props.canLoadMore) {
            this.intersectionObserver = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    if (
                        entry.isIntersecting &&
                        !this.props.loading &&
                        !this.props.loadingMore
                    ) {
                        this.props.onLoadMore();
                    }
                },
                {
                    root: null,
                    rootMargin: '200px', // Start loading 200px before the element becomes visible
                    threshold: 0.1,
                }
            );

            this.intersectionObserver.observe(
                this.loadMoreTriggerRef.current
            );
        }
    };

    cleanupLazyLoading = () => {
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }
    };

    onSelect = (asset, isShiftKeyPressed) => {
        const { selectedAssetsIds, lastSelectedIndex } = this.state;
        const { assets, actions } = this.props;

        if (actions.length === 0) {
            return;
        }

        let affectedAssetsIds = new Set();

        const assetIndex = assets.findIndex(
            (a) => a && a.id === asset.id
        );

        if (lastSelectedIndex === undefined || !isShiftKeyPressed) {
            affectedAssetsIds.add(asset.id);
        } else if (isShiftKeyPressed) {
            // Shift key is pressed, select all assets between the last selected asset and the current asset
            const start = Math.min(lastSelectedIndex, assetIndex);
            const end = Math.max(lastSelectedIndex, assetIndex);

            for (let i = start; i <= end; i += 1) {
                affectedAssetsIds.add(assets[i].id);
            }
        }

        const shouldRemove = selectedAssetsIds.has(asset.id);

        if (shouldRemove) {
            affectedAssetsIds.forEach((id) => selectedAssetsIds.delete(id));
        } else {
            affectedAssetsIds.forEach((id) => selectedAssetsIds.add(id));
        }

        this.setState({
            selectedAssetsIds: new Set([...selectedAssetsIds]),
            lastSelectedIndex: assetIndex,
        });
    };

    onSelectAllClick = () => {
        const { selectedAssetsIds } = this.state;
        const { assets } = this.props;

        if (assets.length === selectedAssetsIds.size) {
            this.setState({ selectedAssetsIds: new Set() });
        } else {
            this.setState({
                selectedAssetsIds: new Set(assets.map((asset) => asset.id)),
            });
        }
    };

    openBatchActionModal = (actionType, assets) => {
        this.setState({
            activeModalType: actionType,
            selectedAssets: assets,
        });
    };

    closeModal = () => {
        this.setState({
            activeModalType: null,
            selectedAssets: [],
        });
    };

    shouldShowModal = (actionType) => {
        const { activeModalType } = this.state;
        return activeModalType === actionType;
    };

    executeBatchAction = (actionType, assets, additionalData) => {
        this.setState({
            activeModalType: null,
            selectedAssets: [],
            selectedAssetsIds: new Set(),
            lastSelectedIndex: undefined,
        });

        this.props.onBatchAction(actionType, assets, additionalData);
    };

    openExportImagesModal = () => {
        this.setState({
            showExportImagesModal: true,
        });
    };

    closeExportImagesModal = () => {
        this.setState({
            showExportImagesModal: false,
        });
    };

    handleExportImagesConfirm = ({ exportTables, exportCharts, format, withBackground }) => {
        const { onCreateExportJob } = this.props;

        if (typeof onCreateExportJob === 'function') {
            onCreateExportJob({
                exportTables,
                exportCharts,
                format,
                withBackground,
                selectedAssetsIds: [...this.state.selectedAssetsIds],
            });
        }

        this.setState({
            showExportImagesModal: false,
            showExportImagesConfirmation: true,
            lastExportSelection: { exportTables, exportCharts, format, withBackground },
        });
    };


    closeExportImagesConfirmation = () => {
        this.setState({
            showExportImagesConfirmation: false,
        });
    };

    render() {
        const {
            application,
            assets,
            loading,
            loadingMore,
            canLoadMore,
            onLoadMore,
            error,
            classes,
            actions,
            isAdmin,
            batchActionsLoading,
        } = this.props;

        const {
            selectedAssetsIds,
            selectedAssets,
            showExportImagesModal,
            showExportImagesConfirmation,
            lastExportSelection,
        } = this.state;

        const inChart = this.props.match.params.application === "chart";

        let content;

        switch (true) {
            case !!error:
                content = <AssetsError error={error} />;
                break;
            case !loading && !assets.length:
                content = <EmptyAssets application={application} />;
                break;
            default:
                const assetList = assets.map((asset) => (
                    <Asset
                        key={asset.id}
                        asset={asset}
                        onSelect={this.onSelect}
                        isSelected={selectedAssetsIds.has(asset.id)}
                        actions={actions}
                        anySelected={selectedAssetsIds.size > 0}
                    />
                ));
                content = (
                    <div className={classes.contentContainer}>
                        {loading && <LinearProgress />}
                        <div className={classes.assetsList}>
                            {assetList}
                            {canLoadMore && (
                                <div
                                    ref={this.loadMoreTriggerRef}
                                    className={classes.lazyLoadTrigger}
                                >
                                    <AssetLoadMore
                                        disabled={loading || loadingMore}
                                        onLoadMore={onLoadMore}
                                    />
                                </div>
                            )}
                            {loadingMore && <LinearProgress />}
                        </div>

                        <BatchCopyToModal
                            show={this.shouldShowModal(
                                BATCH_ACTION_TYPES.COPY_TO
                            )}
                            selectedAssets={selectedAssets}
                            isAdmin={isAdmin}
                            executeBatchAction={this.executeBatchAction}
                            onClose={this.closeModal}
                        />

                        <BatchDuplicateModal
                            show={this.shouldShowModal(
                                BATCH_ACTION_TYPES.DUPLICATE
                            )}
                            selectedAssets={selectedAssets}
                            executeBatchAction={this.executeBatchAction}
                            onClose={this.closeModal}
                        />

                        <BatchMoveToModal
                            show={this.shouldShowModal(
                                BATCH_ACTION_TYPES.MOVE_TO
                            )}
                            selectedAssets={selectedAssets}
                            isAdmin={isAdmin}
                            executeBatchAction={this.executeBatchAction}
                            onClose={this.closeModal}
                        />

                        <BatchDeleteModal
                            show={this.shouldShowModal(
                                BATCH_ACTION_TYPES.DELETE
                            )}
                            selectedAssets={selectedAssets}
                            executeBatchAction={this.executeBatchAction}
                            onClose={this.closeModal}
                        />

                        <BatchTagModal
                            show={this.shouldShowModal(
                                BATCH_ACTION_TYPES.TAG
                            )}
                            selectedAssets={selectedAssets}
                            executeBatchAction={this.executeBatchAction}
                            onClose={this.closeModal}
                        />

                        <BatchPublishModal
                            show={this.shouldShowModal(
                                BATCH_ACTION_TYPES.PUBLISH
                            )}
                            selectedAssets={selectedAssets}
                            executeBatchAction={this.executeBatchAction}
                            onClose={this.closeModal}
                        />

                        <LoadingModal
                            show={batchActionsLoading}
                            message="Processing batch action..."
                        />

                        {/* Project-wide export modals */}
                        <ExportImagesModal
                            show={showExportImagesModal}
                            onClose={this.closeExportImagesModal}
                            onConfirm={this.handleExportImagesConfirm}
                            hasSelection={this.state.selectedAssetsIds.size > 0}
                        />

                        <ExportImagesConfirmationModal
                            show={showExportImagesConfirmation}
                            onClose={this.closeExportImagesConfirmation}
                            selection={lastExportSelection}
                        />
                    </div>
                );
        }

        return (
            <div className={classes.wrapper}>
                {/* Top bar: Sorting on the left, export button on the right */}
                <div className={classes.topBar}>
                    <div className={classes.topBarSorting}>
                        <SortingView />
                    </div>

                    {inChart && <Button variant="outlined" color="primary" className={classes.exportButton} onClick={this.openExportImagesModal}>
                            <span className="material-icons">cloud_download</span> Export project images
                        </Button>}
                </div>

                {actions &&
                    actions.length > 0 &&
                    assets.length > 0 && (
                        <div className={classes.stickyHeader}>
                            <AssetActions
                                assets={assets}
                                actions={actions}
                                selectedAssetsIds={selectedAssetsIds}
                                openBatchActionModal={
                                    this.openBatchActionModal
                                }
                                onSelectAllClick={this.onSelectAllClick}
                            />
                        </div>
                    )}
                {content}
            </div>
        );
    }
}

Assets.propTypes = {
    application: PropTypes.string,
    assets: PropTypes.array,
    loading: PropTypes.bool,
    loadingMore: PropTypes.bool,
    canLoadMore: PropTypes.bool,
    error: PropTypes.object,
    onLoadMore: PropTypes.func,
    onBatchAction: PropTypes.func,
    onFetchTags: PropTypes.func.isRequired,
    onCreateExportJob: PropTypes.func,
    actions: PropTypes.array,
    isAdmin: PropTypes.bool,
    batchActionsLoading: PropTypes.bool,
};

Assets.defaultProps = {
    assets: [],
    loading: false,
    loadingMore: false,
    canLoadMore: false,
    error: undefined,
    application: undefined,
    onLoadMore: () => { },
    onBatchAction: () => { },
    onCreateExportJob: null,
    actions: [],
    isAdmin: false,
    batchActionsLoading: false,
};

const style = ({ spacing }) => ({
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        marginTop: spacing.unit * 6,
        width: '100%',
        position: 'relative',
    },
    holder: {
        width: '100%',
        maxWidth: spacing.maxWidth,
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        // justifyContent: 'space-between',
        width: '100%',
        maxWidth: spacing.maxWidth,
        marginBottom: spacing.unit,
        padding: `0 ${spacing.unit * 2}px`,
    },
    topBarSorting: {
        flexGrow: 1,
        marginRight: spacing.unit * 2,
        overflow: 'hidden',
        paddingTop: spacing.unit
    },
    contentContainer: {
        width: '100%',
        maxWidth: spacing.maxWidth,
        paddingTop: spacing.unit,
    },
    assetsList: {
        width: '100%',
        maxWidth: spacing.maxWidth,
        minHeight: '300px',
    },
    stickyHeader: {
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 100,
        width: '100%',
        maxWidth: spacing.maxWidth,
        marginBottom: spacing.unit,
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    exportButton: {
        whiteSpace: 'nowrap',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        '& span.material-icons': {
            marginRight: spacing.unit,
        },
        borderRadius: '4px',
        padding: `${spacing.unit}px ${spacing.unit * 2}px`,
        border: '1px solid #e0e0e0',
        color: 'rgba(0, 0, 0, 0.54)',
        textTransform: 'none',
        backgroundColor: 'white',
        '&:hover': {
            backgroundColor: '#f5f5f5',
            color: '#424242',
        },
    },
    lazyLoadTrigger: {
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: spacing.unit * 2,
    },
});

export default withRouter(injectSheet(style)(Assets));



// WEBPACK FOOTER //
// ./src/components/assets/Assets.js